import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Heart, Briefcase, BookOpen, GraduationCap, Sparkles, Palette, Brain, Mic, HeartPulse, PenTool, Flower2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import SEOHead from "@/components/SEOHead";
import AuthorByline from "@/components/AuthorByline";
import { buildBreadcrumb, buildService, buildFAQ, CORE_FAQS } from "@/lib/seo/schemas";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const individualServices = [
  { icon: Heart, title: "Life Coaching", desc: "Career clarity, burnout recovery, leadership identity, and work-life integration through guided one-on-one sessions." },
  { icon: Sparkles, title: "Personal Branding & Image Consulting", desc: "Discover your authentic self through personalized style coaching, strategic use of colours, and effective body language." },
  { icon: Brain, title: "Emotional Intelligence & Resilience", desc: "Build mental fitness, overcome limiting beliefs, and develop practical strategies for demanding environments." },
  { icon: PenTool, title: "Graphology (Handwriting Analysis)", desc: "Unlock personality insights, identify behavioural patterns, and guide personal development through scientific handwriting analysis." },
];

const companyServices = [
  { icon: Briefcase, title: "Executive Presence & Leadership Facilitation", desc: "Strategic leadership facilitation and coaching engagements designed to strengthen executive presence, enterprise decision‑making, and leadership impact at senior levels." },
  { icon: GraduationCap, title: "Workplace Soft Skills Excellence", desc: "Enterprise‑grade capability programmes focused on elevating communication, collaboration, and professional effectiveness across organisational layers." },
  { icon: Mic, title: "Communication, Impact & Personal Branding", desc: "A structured approach to enhancing executive communication and professional identity, enabling leaders to influence with clarity, confidence, and consistency." },
  { icon: Palette, title: "Creative Resilience & Stress Management", desc: "Evidence‑informed wellbeing interventions that enable employees to build resilience, manage cognitive overload, and sustain performance in demanding environments." },
  { icon: HeartPulse, title: "Ethical, Safe & Inclusive Workplaces (POSH)", desc: "Certified POSH interventions that reinforce organisational compliance, ethical behaviour, and a culture of respect aligned to regulatory and governance expectations." },
  { icon: Brain, title: "Behavioural Insight & Self‑Awareness Labs", desc: "Insight‑led sessions that enhance self‑awareness and interpersonal effectiveness, enabling improved collaboration, leadership maturity, and team dynamics." },
];

const studentServices = [
  { icon: Mic, title: "Corporate Communication & Professional Etiquette", desc: "Learn workplace communication, email writing, meeting conduct, and corporate norms for a smooth campus-to-corporate transition." },
  { icon: Briefcase, title: "Interview Readiness & Employability Skills", desc: "Practical preparation covering interview presence, CV conversations, and situational responses using real hiring scenarios." },
  { icon: Flower2, title: "Presentation & Public Speaking", desc: "Speak with confidence, clarity, and structure — whether in interviews, presentations, or professional discussions." },
  { icon: HeartPulse, title: "Workplace Behaviour, Ethics & POSH", desc: "Essential awareness of workplace conduct, respect, inclusion, ethics, and boundaries aligned with corporate compliance." },
  { icon: GraduationCap, title: "Time Management & Corporate Mindset", desc: "Shift from academic thinking to professional accountability — prioritisation, ownership, and workplace expectations." },
  { icon: Sparkles, title: "Confidence Building & Career Clarity", desc: "Address self-doubt, career confusion, and transition anxiety through reflective coaching and structured skill-building." },
];

const Services = () => (
  <>
    <SEOHead
      title="Coaching & Corporate Training Services in Dubai | MVIBE"
      description="Explore MVIBE's executive coaching and soft skills training in Dubai for organisations, professionals and students — outcome-led, ICF-aligned programmes."
      schema={[
        buildService({
          name: "Executive coaching and soft skills training",
          description:
            "MVIBE offers ICF-aligned executive coaching, corporate soft skills training, 1:1 life coaching and campus-to-corporate programmes across Dubai, the UAE and India.",
          path: "/services",
        }),
        buildBreadcrumb([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ]),
        buildFAQ(CORE_FAQS.slice(0, 6)),
      ]}
    />

    <section className="bg-primary py-14 text-primary-foreground">
      <div className="container mx-auto px-4 text-center">
        <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
          <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-primary-foreground">Services</span>
        </nav>
        <motion.h1 {...fadeUp()} className="mx-auto max-w-4xl text-3xl font-heading font-bold md:text-5xl lg:text-6xl">
          Services Built Around You
        </motion.h1>
        <motion.p {...fadeUp(0.15)} className="mx-auto mt-3 max-w-2xl text-lg text-primary-foreground/80">
          Whether you're an individual seeking clarity or an organisation investing in your people — MVIBE delivers coaching, training, and consulting that creates lasting change.
        </motion.p>
      </div>
    </section>

    {/* For Individuals */}
    <section className="bg-background py-14">
      <div className="container mx-auto px-4">
        <motion.div {...fadeUp()} className="mb-8 text-center">
          <p className="mb-2 font-sans text-base uppercase tracking-[0.2em] text-primary">For Individuals</p>
          <h2 className="text-3xl font-heading font-bold text-foreground md:text-4xl">Personal Growth & Transformation</h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground">
            One-on-one coaching and consulting designed to help you lead with clarity, confidence, and authenticity.
          </p>
        </motion.div>
        <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2 lg:grid-cols-4">
          {individualServices.map((s, i) => (
            <motion.div key={s.title} {...fadeUp(i * 0.1)} className="rounded-xl border border-border bg-card p-6 transition-shadow hover:shadow-lg">
              <s.icon className="mb-3 h-7 w-7 text-accent" />
              <h3 className="mb-1.5 text-lg font-heading font-bold text-foreground">{s.title}</h3>
              <p className="text-base leading-relaxed text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button asChild size="lg" className="bg-accent font-semibold text-primary hover:bg-accent/90">
            <Link to="/services/individuals">Explore Services for Individuals →</Link>
          </Button>
        </div>
      </div>
    </section>

    {/* For Organisations */}
    <section className="bg-muted/40 py-14">
      <div className="container mx-auto px-4">
        <motion.div {...fadeUp()} className="mb-8 text-center">
          <p className="mb-2 font-sans text-base uppercase tracking-[0.2em] text-primary">For Organisations</p>
          <h2 className="text-3xl font-heading font-bold text-foreground md:text-4xl">Corporate Training & Facilitation</h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground">
            Programmes tailored to your organisation's goals — from leadership offsites to campus-to-corporate readiness.
          </p>
        </motion.div>
        <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
          {companyServices.map((s, i) => (
            <motion.div key={s.title} {...fadeUp(i * 0.1)} className="rounded-xl border border-border bg-card p-6 transition-shadow hover:shadow-lg">
              <s.icon className="mb-3 h-7 w-7 text-accent" />
              <h3 className="mb-1.5 text-lg font-heading font-bold text-foreground">{s.title}</h3>
              <p className="text-base leading-relaxed text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button asChild size="lg" className="bg-accent font-semibold text-primary hover:bg-accent/90">
            <Link to="/services/companies">Explore Services for Corporates →</Link>
          </Button>
        </div>
      </div>
    </section>

    {/* For Students */}
    <section className="bg-background py-14">
      <div className="container mx-auto px-4">
        <motion.div {...fadeUp()} className="mb-8 text-center">
          <p className="mb-2 font-sans text-base uppercase tracking-[0.2em] text-primary">For Students</p>
          <h2 className="text-3xl font-heading font-bold text-foreground md:text-4xl">Campus to Corporate Programmes</h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground">
            Preparing final-year students and fresh graduates for real-world careers with confidence, clarity & capability.
          </p>
        </motion.div>
        <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
          {studentServices.map((s, i) => (
            <motion.div key={s.title} {...fadeUp(i * 0.1)} className="rounded-xl border border-border bg-card p-6 transition-shadow hover:shadow-lg">
              <s.icon className="mb-3 h-7 w-7 text-accent" />
              <h3 className="mb-1.5 text-lg font-heading font-bold text-foreground">{s.title}</h3>
              <p className="text-base leading-relaxed text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button asChild size="lg" className="bg-accent font-semibold text-primary hover:bg-accent/90">
            <Link to="/services/students">Explore Services for Students →</Link>
          </Button>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="bg-accent py-10">
      <div className="container mx-auto px-4 text-center">
        <h2 className="mb-3 text-xl font-heading font-bold text-accent-foreground md:text-3xl">Not sure which service fits?</h2>
        <p className="mx-auto mb-5 max-w-xl text-base text-accent-foreground/80">Book a free discovery call and Mahirah will help you find the right path.</p>
        <DiscoveryCallDialog>
          <Button size="lg" className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
            Book a Discovery Call
          </Button>
        </DiscoveryCallDialog>
      </div>
    </section>

    <section className="bg-background py-10">
      <div className="container mx-auto px-4">
        <AuthorByline />
      </div>
    </section>
  </>
);

export default Services;
